import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SearchableSelect from "@/components/SearchableSelect";
import api, { formatDetail } from "@/lib/api";
import { fmtDate, formatIDR } from "@/lib/constants";
import { SuggestBadge } from "./LegacyLinkBadge";
import { RANGE_PRESETS, rangeFor, toJakartaDate } from "@/lib/dateRange";

const ACTIONS = [
  ["petty_cash", "Proses sebagai petty cash"], ["bank", "Proses sebagai transaksi rekening bank"],
  ["link_canonical", "Tautkan ke transaksi kanonis yang sudah ada"], ["mark_duplicate", "Tandai duplikat"],
  ["not_project", "Bukan pengeluaran proyek"], ["unknown", "Sumber belum dapat ditentukan"], ["cancel", "Batalkan transaksi"],
];
const INFO = {
  petty_cash: "Wajib pilih pemegang & pengajuan Weekly Petty Cash yang sudah ditransfer. Realisasi baru masuk Menunggu Verifikasi FAT; baru setelah diverifikasi masuk Cost Control & mengurangi saldo.",
  bank: "Transaksi Buku Bank/e-statement terpilih menjadi sumber biaya kanonis (badge BUKU BANK — CATAT PENGELUARAN). Tidak membuat realisasi petty cash & tidak mengubah saldo petty cash.",
  link_canonical: "Entri lama dipetakan ke realisasi Catat Pengeluaran yang sudah ada — hanya satu yang dihitung.",
  mark_duplicate: "Entri lama dikeluarkan dari Uang Keluar; transaksi kanonisnya yang dihitung. Tidak dihapus.",
  not_project: "Entri lama dikeluarkan dari Uang Keluar proyek. Tidak dihapus.",
  unknown: "Tetap dihitung seperti sekarang, tidak masuk Cost Control, tetap di antrean untuk diperiksa ulang.",
  cancel: "Entri lama dikeluarkan dari Uang Keluar. Tidak dihapus.",
};
const SEL = "w-full h-9 border border-slate-300 px-2 text-xs bg-white";

function PettyCashPick({ v, setV }) {
  const [holders, setHolders] = useState([]);
  const [subs, setSubs] = useState([]);
  useEffect(() => { api.get("/pc-manual-realizations/funding-options").then(({ data }) => setHolders(data.holders || [])).catch(() => {}); }, []);
  useEffect(() => {
    setSubs([]);
    if (v.holder_id) api.get("/pc-manual-realizations/funding-options", { params: { holder_id: v.holder_id, include_items: true } }).then(({ data }) => setSubs(data.submissions || [])).catch(() => {});
  }, [v.holder_id]);
  const items = subs.find((s) => s.id === v.pc_id)?.items || [];
  return (
    <div className="space-y-2" data-testid="lrd-pc">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <SearchableSelect testid="lrd-holder" value={v.holder_id} onChange={(x) => setV({ ...v, holder_id: x, pc_id: "", pc_item_id: "" })} options={holders.map((h) => ({ value: h.id, label: h.name }))} placeholder="Pemegang petty cash…" searchPlaceholder="Ketik nama…" emptyText="Tidak ada" />
        <SearchableSelect testid="lrd-pc-sub" value={v.pc_id} onChange={(x) => setV({ ...v, pc_id: x, pc_item_id: "" })} options={subs.map((s) => ({ value: s.id, label: `${s.week_label || "-"} · ${s.project_name || "-"}` }))} placeholder={v.holder_id ? "Pengajuan ditransfer…" : "Pilih pemegang dulu"} searchPlaceholder="Ketik…" emptyText="Tidak ada" />
      </div>
      <div className="inline-flex border border-slate-300" data-testid="lrd-pc-mode">
        {[["new", "Konfirmasi sebagai realisasi baru"], ["existing", "Tautkan ke realisasi yang sudah ada"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setV({ ...v, pc_mode: k })} data-testid={`lrd-pc-mode-${k}`}
            className={`px-2 py-1 text-[10px] uppercase tracking-wider font-semibold ${v.pc_mode === k ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600"}`}>{l}</button>
        ))}
      </div>
      {v.pc_mode === "existing" && (
        <select value={v.pc_item_id} onChange={(e) => setV({ ...v, pc_item_id: e.target.value })} data-testid="lrd-pc-item" className={SEL}>
          <option value="">Pilih item realisasi…</option>
          {items.map((it) => <option key={it.id} value={it.id}>{fmtDate(it.date)} · {it.name} · {formatIDR(it.actual_amount)}</option>)}
        </select>
      )}
    </div>
  );
}

function BankPick({ e, v, setV }) {
  const [banks, setBanks] = useState([]);
  const txDate = toJakartaDate(e.date);
  const init = rangeFor(txDate, "7");
  const [preset, setPreset] = useState(txDate ? "7" : "");
  const [flt, setFlt] = useState({ account_id: "", date_from: init.from, date_to: init.to, amount: String(e.amount || ""), q: "", ref: "" });
  const [rows, setRows] = useState({ items: [], total: 0, diagnosis: null });
  const applyPreset = (p) => { const r = rangeFor(txDate, p); setPreset(p); setFlt({ ...flt, date_from: r.from, date_to: r.to }); };
  useEffect(() => { api.get("/company-bank-accounts").then(({ data }) => setBanks(Array.isArray(data) ? data : data.items || [])).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      const params = { project_id: e.project_id, page_size: 50, anchor_date: txDate || undefined, target_amount: e.amount || undefined,
        hint: [e.note, e.reference_no].filter(Boolean).join(" ") || undefined, ...Object.fromEntries(Object.entries(flt).filter(([, x]) => x !== "")) };
      api.get("/pc-manual-realizations-legacy/bank-candidates", { params }).then(({ data }) => setRows(data)).catch(() => setRows({ items: [], total: 0, diagnosis: null }));
    }, 300);
    return () => clearTimeout(t);
  }, [flt, e.project_id]);
  const set = (k) => (ev) => { if (k === "date_from" || k === "date_to") setPreset(""); setFlt({ ...flt, [k]: ev.target.value }); };
  return (
    <div className="space-y-2" data-testid="lrd-bank">
      <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
        <span className="text-slate-500">Rentang:</span>
        {RANGE_PRESETS.map(([k, l]) => (
          <button key={k} type="button" disabled={!txDate} onClick={() => applyPreset(k)} data-testid={`lrd-range-${k}`}
            className={`px-2 py-0.5 border uppercase tracking-wider font-semibold disabled:opacity-40 ${preset === k ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "border-slate-300 text-slate-600"}`}>{l}</button>
        ))}
        {!txDate && <span className="text-amber-700" data-testid="lrd-range-missing">Tanggal transaksi lama tidak tersedia — pilih rentang tanggal manual.</span>}
        {e.legacy_source && <span className="text-slate-500" data-testid="lrd-bank-suggest">Saran rekening: {e.legacy_source} (tidak dipilih otomatis)</span>}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
        <select value={flt.account_id} onChange={set("account_id")} data-testid="lrd-bank-account" className={SEL}><option value="">Pilih rekening…</option>{banks.map((b) => <option key={b.id} value={b.id}>{b.bank_name} {b.account_no}</option>)}</select>
        <input type="date" value={flt.date_from} onChange={set("date_from")} data-testid="lrd-bank-from" className={SEL} />
        <input type="date" value={flt.date_to} onChange={set("date_to")} data-testid="lrd-bank-to" className={SEL} />
        <input value={flt.amount} onChange={set("amount")} placeholder="Nominal" data-testid="lrd-bank-amount" className={SEL} />
        <input value={flt.q} onChange={set("q")} placeholder="Deskripsi" data-testid="lrd-bank-q" className={SEL} />
        <input value={flt.ref} onChange={set("ref")} placeholder="No. referensi" data-testid="lrd-bank-ref" className={SEL} />
      </div>
      <div className="max-h-52 overflow-y-auto border border-slate-200" data-testid="lrd-bank-list">
        {rows.items.length ? rows.items.map((m) => (
          <label key={m.movement_id} className={`flex items-start gap-2 px-2 py-1.5 border-b border-slate-100 text-[11px] cursor-pointer ${v.movement_id === m.movement_id ? "bg-sky-50" : ""}`} data-testid={`lrd-bank-opt-${m.movement_id}`}>
            <input type="radio" checked={v.movement_id === m.movement_id} onChange={() => setV({ ...v, movement_id: m.movement_id })} className="mt-0.5" />
            <span className="flex-1">{fmtDate(m.date)} · {m.account_label} · {m.description || "—"}{m.reference_no ? ` · ${m.reference_no}` : ""}<span className="block text-[10px] text-slate-500">{m.source}{m.classified ? ` · Pembayaran ${m.project_name || ""}` : " · belum diklasifikasi"}</span></span>
            <span className="font-semibold tabular-nums">{formatIDR(m.amount)}</span>
          </label>
        )) : <EmptyBank rows={rows} flt={flt} />}
      </div>
      <p className="text-[10px] text-slate-500" data-testid="lrd-bank-count">{rows.total} transaksi debit belum tertaut{rows.total > rows.items.length ? ` (menampilkan ${rows.items.length} — persempit filter)` : ""}. Urutan: tanggal terdekat, nominal terdekat, kecocokan deskripsi — pilih sendiri.</p>
    </div>
  );
}

function EmptyBank({ rows, flt }) {
  const per = flt.date_from || flt.date_to ? `${flt.date_from ? fmtDate(flt.date_from) : "…"} – ${flt.date_to ? fmtDate(flt.date_to) : "…"}` : "semua periode";
  const acc = rows.account_label || (flt.account_id ? "rekening terpilih" : "semua rekening");
  const msg = {
    no_statement: `Belum ada e-statement yang diimpor untuk ${acc} pada periode ${per}.`,
    no_match: "E-statement tersedia, tetapi tidak ditemukan transaksi debit yang sesuai dengan filter.",
    all_linked: "Transaksi ditemukan, tetapi semuanya sudah tertaut ke data lain.",
  }[rows.diagnosis] || "Memuat…";
  return <p className="px-2 py-4 text-center text-[11px] italic text-slate-500" data-testid={`lrd-bank-empty-${rows.diagnosis || "loading"}`}>{msg}</p>;
}

function bodyFor(v) {
  if (v.action === "petty_cash") {
    return v.pc_mode === "existing"
      ? { action: "petty_cash_existing", holder_id: v.holder_id, pc_id: v.pc_id, pc_item_id: v.pc_item_id }
      : { action: "petty_cash_new", holder_id: v.holder_id, pc_id: v.pc_id };
  }
  if (v.action === "bank") return { action: "bank", movement_id: v.movement_id };
  if (v.action === "link_canonical") return { action: "link_canonical", realization_id: v.realization_id };
  return { action: v.action, duplicate_of: v.duplicate_of || null };
}

function ready(v) {
  if (!v.action || !v.reason.trim()) return false;
  if (v.action === "petty_cash") return !!(v.holder_id && v.pc_id && v.pc_mode && (v.pc_mode === "new" || v.pc_item_id));
  if (v.action === "bank") return !!v.movement_id;
  if (v.action === "link_canonical") return !!v.realization_id;
  return true;
}

function CanonicalPick({ e, v, setV }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/pc-manual-realizations", { params: { project_id: e.project_id } }).then(({ data }) => setRows((data.items || []).filter((r) => ["pending_fat", "verified"].includes(r.status) && !r.legacy_entry_ref))).catch(() => {});
  }, [e.project_id]);
  return (
    <select value={v.realization_id} onChange={(ev) => setV({ ...v, realization_id: ev.target.value })} data-testid="lrd-canonical" className={SEL}>
      <option value="">Pilih realisasi Catat Pengeluaran…</option>
      {rows.map((r) => <option key={r.id} value={r.id}>{fmtDate(r.transaction_date)} · {r.description} · {formatIDR(r.amount)} · {r.status_label}</option>)}
    </select>
  );
}

export default function LegacyResolveDialog({ act, onClose, onDone }) {
  const { e, kind } = act;
  const [v, setV] = useState({ action: "", reason: "", holder_id: "", pc_id: "", pc_mode: "", pc_item_id: "", movement_id: "", realization_id: "", duplicate_of: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      if (kind === "reopen") await api.post(`/pc-manual-realizations-legacy/${e.project_id}/${e.entry_id}/reopen`, { reason: v.reason });
      else await api.post(`/pc-manual-realizations-legacy/${e.project_id}/${e.entry_id}/resolve`, { ...bodyFor(v), reason: v.reason });
      toast.success("Tersimpan"); onDone();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Gagal menyimpan"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="lrd-dialog">
        <DialogHeader><DialogTitle className="text-sm uppercase tracking-wider">{kind === "reopen" ? "Buka Kembali Pengeluaran Lama" : "Proses Pengeluaran Manual Lama"}</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-600 border border-slate-200 bg-slate-50 px-2 py-1.5 space-y-1">
          <div>{fmtDate(e.date)} · {e.project_name || "-"} · {e.note || "—"} · <b>{formatIDR(e.amount)}</b></div>
          <div className="text-[10px]">Sumber lama: {e.legacy_source || "—"}</div>
          <SuggestBadge s={e.suggestion} testid="lrd-suggestion" />
        </div>
        {kind !== "reopen" && (
          <>
            <select value={v.action} onChange={(ev) => setV({ ...v, action: ev.target.value })} data-testid="lrd-action" className={SEL}>
              <option value="">Pilih tindakan…</option>
              {ACTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            {v.action && <p className="text-[10px] text-slate-500 border-l-2 border-slate-300 pl-2" data-testid="lrd-info">{INFO[v.action]}</p>}
            {v.action === "petty_cash" && <PettyCashPick v={v} setV={setV} />}
            {v.action === "bank" && <BankPick e={e} v={v} setV={setV} />}
            {v.action === "link_canonical" && <CanonicalPick e={e} v={v} setV={setV} />}
          </>
        )}
        <textarea rows={2} value={v.reason} onChange={(ev) => setV({ ...v, reason: ev.target.value })} placeholder="Alasan (wajib)" data-testid="lrd-reason" className="w-full text-xs border border-slate-300 px-2 py-1.5" />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-9 px-3 border border-slate-300 text-xs uppercase tracking-wider font-semibold" data-testid="lrd-close">Tutup</button>
          <button type="button" disabled={busy || (kind === "reopen" ? !v.reason.trim() : !ready(v))} onClick={submit} data-testid="lrd-submit"
            className="h-9 px-4 bg-[#0B1D3A] text-white text-xs uppercase tracking-wider font-semibold disabled:opacity-50">{busy ? "Menyimpan…" : "Simpan"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
